import type {
  ArticleKind,
  GrammaticalCase,
  GrammaticalNumber,
  Locale,
  MessageParams,
  NounRequest,
  NounTable,
} from './types';
import { renderNounDe } from './grammar/de';
import { renderNounEn } from './grammar/en';

/**
 * Minimaler Nachrichten-Formatter mit ICU-aehnlicher Syntax plus einer
 * Substantiv-Erweiterung fuer die deutsche Flexion.
 *
 * Unterstuetzte Platzhalter:
 *   {name}                                        einfacher Wert
 *   {count, plural, one {# Teil} other {# Teile}} Pluralwahl, `#` wird zur Zahl
 *   {kind, select, alien {…} other {…}}           Fallunterscheidung
 *   {noun:wreck|art=indef|case=acc|num=pl|caps}   flektiertes Substantiv
 *
 * Bewusst selbst implementiert statt per ICU-Bibliothek: keine gaengige
 * Bibliothek beherrscht deutsche Kasusflexion, und genau die ist hier das
 * eigentliche Problem. Der Rest ist wenig Code und vollstaendig testbar.
 */

const NOUN_DEFAULTS = {
  article: 'def' as ArticleKind,
  case: 'nom' as GrammaticalCase,
  number: 'sg' as GrammaticalNumber,
  caps: false,
};

const ARTICLE_ALIASES: Readonly<Record<string, ArticleKind>> = {
  def: 'def',
  indef: 'indef',
  none: 'none',
};

const CASE_ALIASES: Readonly<Record<string, GrammaticalCase>> = {
  nom: 'nom',
  acc: 'acc',
  dat: 'dat',
  gen: 'gen',
};

const NUMBER_ALIASES: Readonly<Record<string, GrammaticalNumber>> = {
  sg: 'sg',
  pl: 'pl',
};

/**
 * Findet zur oeffnenden Klammer bei `start` die zugehoerige schliessende.
 * Zaehlt die Schachtelungstiefe, damit `{a, plural, one {{x}} other {…}}`
 * korrekt zerlegt wird. Liefert -1, wenn die Klammer unbalanciert ist.
 */
function matchBrace(input: string, start: number): number {
  let depth = 0;
  for (let i = start; i < input.length; i++) {
    const ch = input[i];
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

interface PluralOption {
  readonly selector: string;
  readonly body: string;
}

/** Zerlegt `one {…} other {…}` in seine Zweige. */
function parseOptions(input: string): PluralOption[] {
  const options: PluralOption[] = [];
  let i = 0;

  while (i < input.length) {
    while (i < input.length && /\s/.test(input[i] as string)) i++;
    if (i >= input.length) break;

    const selectorStart = i;
    while (i < input.length && !/[\s{]/.test(input[i] as string)) i++;
    const selector = input.slice(selectorStart, i);

    while (i < input.length && /\s/.test(input[i] as string)) i++;
    if (input[i] !== '{') break;

    const end = matchBrace(input, i);
    if (end === -1) break;

    options.push({ selector, body: input.slice(i + 1, end) });
    i = end + 1;
  }

  return options;
}

function selectPluralBody(
  options: readonly PluralOption[],
  value: number,
  locale: Locale,
): string | null {
  const exact = options.find((o) => o.selector === `=${value}`);
  if (exact !== undefined) return exact.body;

  const category = new Intl.PluralRules(locale).select(value);
  const match = options.find((o) => o.selector === category);
  if (match !== undefined) return match.body;

  return options.find((o) => o.selector === 'other')?.body ?? null;
}

function selectBody(options: readonly PluralOption[], value: string): string | null {
  const match = options.find((o) => o.selector === value);
  if (match !== undefined) return match.body;
  return options.find((o) => o.selector === 'other')?.body ?? null;
}

/** Zerlegt `noun:wreck|art=indef|case=acc|prep=von|caps` in eine NounRequest. */
function parseNounRequest(spec: string): NounRequest {
  const parts = spec.split('|');
  const id = (parts[0] ?? '').slice('noun:'.length).trim();

  let article = NOUN_DEFAULTS.article;
  let grammaticalCase = NOUN_DEFAULTS.case;
  let number = NOUN_DEFAULTS.number;
  let caps = NOUN_DEFAULTS.caps;
  let prep: string | undefined;

  for (const raw of parts.slice(1)) {
    const token = raw.trim();
    if (token === '') continue;

    if (token === 'caps') {
      caps = true;
      continue;
    }

    const eq = token.indexOf('=');
    if (eq === -1) continue;

    const key = token.slice(0, eq).trim();
    const value = token.slice(eq + 1).trim();

    if (key === 'art') article = ARTICLE_ALIASES[value] ?? article;
    else if (key === 'case') grammaticalCase = CASE_ALIASES[value] ?? grammaticalCase;
    else if (key === 'num') number = NUMBER_ALIASES[value] ?? number;
    else if (key === 'prep') prep = value;
  }

  const base = { id, article, case: grammaticalCase, number, caps };
  return prep === undefined ? base : { ...base, prep };
}

function renderNoun(req: NounRequest, locale: Locale, nouns: NounTable): string {
  return locale === 'de' ? renderNounDe(req, nouns) : renderNounEn(req, nouns);
}

export interface FormatContext {
  readonly locale: Locale;
  readonly nouns: NounTable;
}

/**
 * Setzt eine Vorlage mit Parametern zusammen.
 * `hashValue` traegt den Zahlwert eines umschliessenden Plural-Zweigs, damit `#`
 * auch in geschachtelten Vorlagen aufgeloest werden kann.
 */
export function formatMessage(
  template: string,
  params: MessageParams,
  ctx: FormatContext,
  hashValue?: number,
): string {
  let out = '';
  let i = 0;

  while (i < template.length) {
    const ch = template[i] as string;

    if (ch === '#' && hashValue !== undefined) {
      out += new Intl.NumberFormat(ctx.locale).format(hashValue);
      i++;
      continue;
    }

    if (ch !== '{') {
      out += ch;
      i++;
      continue;
    }

    const end = matchBrace(template, i);
    if (end === -1) {
      // Unbalancierte Klammer: Rest woertlich ausgeben statt zu werfen.
      out += template.slice(i);
      break;
    }

    const inner = template.slice(i + 1, end).trim();
    out += renderPlaceholder(inner, params, ctx, hashValue);
    i = end + 1;
  }

  return out;
}

function renderPlaceholder(
  inner: string,
  params: MessageParams,
  ctx: FormatContext,
  hashValue: number | undefined,
): string {
  if (inner.startsWith('noun:')) {
    return renderNoun(parseNounRequest(inner), ctx.locale, ctx.nouns);
  }

  const firstComma = inner.indexOf(',');
  if (firstComma === -1) {
    const value = params[inner];
    if (value === undefined) return `⟨${inner}?⟩`;
    return typeof value === 'number'
      ? new Intl.NumberFormat(ctx.locale).format(value)
      : String(value);
  }

  const name = inner.slice(0, firstComma).trim();
  const rest = inner.slice(firstComma + 1);
  const secondComma = rest.indexOf(',');
  if (secondComma === -1) return `⟨${name}?⟩`;

  const kind = rest.slice(0, secondComma).trim();
  const options = parseOptions(rest.slice(secondComma + 1));
  const value = params[name];

  if (kind === 'plural') {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return `⟨${name}?⟩`;
    const body = selectPluralBody(options, num, ctx.locale);
    return body === null ? `⟨${name}?⟩` : formatMessage(body, params, ctx, num);
  }

  if (kind === 'select') {
    const body = selectBody(options, String(value));
    return body === null ? `⟨${name}?⟩` : formatMessage(body, params, ctx, hashValue);
  }

  return `⟨${name}?⟩`;
}
